import React, { useMemo, useRef, useState } from 'react';

import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import {
  Input,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue
} from '../../atoms';

type Option = {
  value: string;
  label: string;
  [key: string]: any;
};

type ComboBoxProps = {
  options: Option[];
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  value?: string; // Controlled value
  defaultValue?: string; // Default value for uncontrolled behavior
  onValueChange: (value: string) => void;
  renderSelectedOption?: (
    selectedOption: Option | undefined
  ) => React.ReactNode; // Custom trigger rendering
  renderOption?: (option: Option) => React.ReactNode; // Custom option rendering
  onClear?: () => any;
  inputPlaceholder?: string;
};

export function ComboBox({
  options,
  placeholder = 'Select',
  className,
  value,
  disabled,
  defaultValue,
  onValueChange,
  renderSelectedOption,
  renderOption,
  inputPlaceholder = 'Search...',
  onClear
}: ComboBoxProps) {
  const [internalValue, setInternalValue] = useState<string | undefined>(
    value ?? defaultValue
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState('');
  const [open, setOpen] = useState(false);

  const selectedValue = useMemo(() => {
    const isControlled = value !== undefined;
    const selectedVal = isControlled ? value : internalValue;
    return selectedVal;
  }, [options, value, internalValue, defaultValue]);

  const selectedOption = useMemo(
    () => options.find(option => option.value === selectedValue),
    [options, selectedValue]
  );

  const filteredOptions = useMemo(
    () =>
      options?.filter(opt =>
        opt.label.toLowerCase().includes(searchInput.toLowerCase())
      ),
    [options, searchInput]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleOptionSelect = (option: Option) => {
    onValueChange(option.value);
    setInternalValue(option.value);
    setOpen(false);
  };

  const handleClear = () => {
    if (onClear) onClear();
    setInternalValue(undefined);
  };

  return (
    <Select
      autoComplete={undefined}
      onOpenChange={open => {
        if (!open) setOpen(false);
      }}
      open={open}
      disabled={disabled}
      value={selectedValue}
      defaultValue={defaultValue}
    >
      <SelectTrigger
        onClick={() => {
          setOpen(!open);
        }}
        className={cn(
          className,
          'outline-none bg-background w-full h-10 text-primary focus:ring-0 [&>svg]:hidden border-input disabled:cursor-default shadow-inpt rounded-lg',
          {
            'text-tertiary font-normal': !selectedValue
          }
        )}
      >
        <SelectValue placeholder={placeholder}>
          {renderSelectedOption ? (
            renderSelectedOption(selectedOption)
          ) : (
            <span>{selectedValue ? selectedOption?.label : placeholder}</span>
          )}
        </SelectValue>
        <div className='flex items-center gap-1'>
          {onClear && !!selectedValue && (
            <div
              onClick={handleClear}
              className='hover:bg-secondary bg-tertiary h-4 w-4 animate-fade-in border-text_tertiary p-[2px]  flex items-center justify-center rounded-full'
            >
              <X
                className='!block w-full h-full text-tertiary transition-all duration-500'
                strokeWidth={2.5}
              />{' '}
            </div>
          )}
          <ChevronDown className='!block w-4 h-4 text-tertiary ' />
        </div>
      </SelectTrigger>
      <SelectContent hideScrollButtons className='p-1'>
        <div className='sticky top-0 z-[52] -translate-y-2 pt-2 bg-card'>
          <Input
            autoFocus
            ref={inputRef}
            placeholder={inputPlaceholder}
            value={searchInput}
            onChange={handleInputChange}
          />
        </div>
        <div className='max-h-[30%]'>
          {filteredOptions.map(option => {
            const isSelected = option.value === selectedOption?.value;
            return (
              <div
                onClick={() => {
                  handleOptionSelect(option);
                }}
                className='hover:bg-tertiary cursor-pointer focus:bg-tertiary relative flex w-full select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 font-medium'
                key={option.value}
              >
                {renderOption ? (
                  renderOption(option)
                ) : (
                  <span className='dark:text-white'>{option.label}</span>
                )}
                {isSelected && (
                  <span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
                    <>
                      <Check className='h-4 w-4' />
                    </>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </SelectContent>
    </Select>
  );
}
